// The top of the campaign editor's right column once a campaign is saved:
// what the provider is doing with it today, in five lines.
//
// Until 2026-09-18 the column led with the publish sequence, so a campaign
// that had been delivering for three weeks opened on "Ads created 2 of 4 ·
// 544h42m" — a stale, miscounted record of a publish that finished on day
// one. The publish log still exists below; this is the current state.
//
// Everything here is read from three sources the rest of the app already
// keeps: the sync status (provider state, ad sets, ads), the campaign overview
// row (spend, impressions, pacing, reporting cutoff) and the plan's window.
// The pure model is exported so it can be tested without a DOM.
//
// 2026-10-19: a campaign whose flights are all over says it ended, what it
// ran and what it spent of its budget, never "day 33 of 33" or "so far";
// one between flights names the next flight.
//
// 2026-10-21 (campaign lifecycle 2a): an archived campaign says it is
// stopping until the provider is seen stopped after the archive, and that the
// stop is not confirmed when it is still seen delivering; a finished The
// Trade Desk campaign's ad groups are switched off by SAM, so "ad groups
// still on" is a short wait, no longer how it ends.

import type { CampaignSyncStatus, CampaignSyncTarget } from '@/api/manage-types';
import type {
  CampaignOverviewRow,
} from '@/api/types';
import { fmt } from '@/components/primitives';
import { providerStatusClass, type ProviderStatusClass } from '@/lib/campaign-publish-sequence';
import { campaignPublishOutcome } from '@/lib/campaign-sync-outcome';
import { fmtDayShort } from '@/lib/datetime';

export interface CampaignDeliverySummaryProps {
  target: CampaignSyncTarget;
  sync: CampaignSyncStatus | undefined;
  /** The campaign window, `YYYY-MM-DD`. */
  startDate: string;
  endDate: string;
  todayStr: string;
  /** This campaign's overview row, when the reporting read has landed. */
  overview?: CampaignOverviewRow | null;
  /** The newest complete delivery day the overview covered. */
  deliveryCutoff?: string | null;
  /** The client's gross budget, in dollars. */
  budget: number;
}

export type DeliveryTone = 'live' | 'quiet' | 'warn' | 'bad';

export interface DeliverySummaryModel {
  headline: string;
  tone: DeliveryTone;
  /** "Since Aug 26 · day 24 of 33 · last report Sep 17" */
  since: string;
  rows: { label: string; value: string }[];
  pace?: {
    /** Share of the budget spent; `null` when no spend is known yet. */
    spentPct: number | null;
    /** How far through the window today is; absent once the flights are over. */
    elapsedPct?: number;
    sentence: string;
    tone: DeliveryTone;
  };
}

const PROVIDER = { meta: 'Meta', ttd: 'The Trade Desk' } as const;

function dayIndex(iso: string, base: string): number {
  return Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${base}T00:00:00Z`)) / 86_400_000);
}

const asNumber = (value: bigint | number | null | undefined): number | null =>
  value === null || value === undefined ? null : Number(value);

/**
 * "2 live" / "1 live · 1 paused" / "3 active · 1 paused · 1 needs review".
 * Objects the provider does not have yet are the publish log's business and
 * are not counted here; zero buckets are left out, except the leading one.
 */
function bucketLine(classes: ProviderStatusClass[], activeWord: 'live' | 'active'): string {
  const count = (cls: ProviderStatusClass) => classes.filter((value) => value === cls).length;
  const paused = count('paused');
  const attention = count('attention');
  return [
    `${count('active')} ${activeWord}`,
    paused > 0 ? `${paused} paused` : '',
    attention > 0 ? `${attention} needs review` : '',
  ].filter(Boolean).join(' · ');
}

export function deliverySummaryModel(props: CampaignDeliverySummaryProps): DeliverySummaryModel | null {
  const { target, sync, startDate, endDate, todayStr, overview, deliveryCutoff, budget } = props;
  if (!sync) return null;
  const provider = PROVIDER[target];
  const outcome = campaignPublishOutcome(sync, target);
  const block = sync[target];
  const operationalStatus = sync.operational_status;
  const operational = operationalStatus?.state;
  const providerState = block?.provider_state;
  // Every flight is over, or the campaign was archived: whatever the provider
  // still reports, this is a campaign that ran (2026-10-19).
  const finished = operationalStatus?.phase === 'finished';
  // Archived: the stored status is `ended` from the archive on, whatever the
  // provider has been seen doing since (2026-10-21).
  const archived = operationalStatus?.requested_state === 'ended';
  const over = finished || archived || operational === 'ended';
  const lastDay = operationalStatus?.last_flight_end ?? endDate;

  // Each state in its own words. Until 2026-09-18 `needs_attention` read as
  // "Partly delivering", `pausing` as "Paused", `starting` as "Live", and a
  // failed publish outcome hid a system pause the observer had confirmed.
  let headline: string;
  let tone: DeliveryTone;
  if (outcome === 'local_ready') {
    headline = 'Not sent to the provider';
    tone = 'quiet';
  } else if (outcome === 'pending' && !archived) {
    // An archived campaign's pending sync is its stop on the way, not a publish.
    headline = `Publishing to ${provider}…`;
    tone = 'quiet';
  } else if (over) {
    // Ahead of every paused and scheduled reading: the flight reconcile pauses
    // a finished Meta campaign, and that pause is the end, not a wait for the
    // next flight. The server says whether the stop was seen.
    if (operational === 'ended') {
      headline = `Ended on ${provider}`;
      tone = 'quiet';
    } else if (archived && operational === 'needs_attention') {
      headline = `Stop not confirmed · still delivering on ${provider}`;
      tone = 'bad';
    } else if (archived) {
      // The archive's stop is on its way: the next check after it says whether it landed.
      headline = `Stopping delivery on ${provider}`;
      tone = 'quiet';
    } else if (operational === 'needs_attention') {
      headline = `Flight ended · still delivering on ${provider}`;
      tone = 'bad';
    } else if (target === 'ttd' && operationalStatus?.observed_state === 'active' && !operationalStatus.stale) {
      // TTD's ad groups stay on after the last flight until SAM's next pass
      // switches them off; the flight window stops spend meanwhile.
      headline = `Ended on ${provider} · ad groups still on`;
      tone = 'quiet';
    } else {
      headline = `Ended on ${provider} · stop not confirmed`;
      tone = 'warn';
    }
  } else if (operational === 'paused_by_system') {
    // The observer confirmed SAM stopped this campaign; that outranks a stale
    // publish verdict, which the publish log below still carries.
    headline = `Paused by SAM on ${provider}`;
    tone = 'bad';
  } else if (outcome === 'failed') {
    headline = `Publish failed on ${provider}`;
    tone = 'bad';
  } else if (operational === 'pausing') {
    headline = `Pausing on ${provider}…`;
    tone = 'warn';
  } else if (operational === 'paused'
    || providerState === 'paused_superseded' || providerState === 'disabled' || providerState === 'disabled_superseded') {
    headline = `Paused on ${provider}`;
    tone = 'warn';
  } else if (operational === 'needs_attention') {
    headline = `Needs attention on ${provider}`;
    tone = 'warn';
  } else if (operational === 'partially_active') {
    headline = `Partly delivering on ${provider}`;
    tone = 'warn';
  } else if (operational === 'scheduled' && operationalStatus?.phase === 'between_flights' && operationalStatus.next_flight_date) {
    headline = `Between flights · next flight ${fmtDayShort(operationalStatus.next_flight_date)}`;
    tone = 'quiet';
  } else if (providerState === 'scheduled_paused' || operational === 'scheduled') {
    headline = `Built on ${provider}, waiting for its flight`;
    tone = 'quiet';
  } else if (operational === 'starting') {
    headline = `Starting on ${provider}`;
    tone = 'quiet';
  } else if (providerState === 'active_pending_delivery') {
    headline = `Live on ${provider}, no delivery counted yet`;
    tone = 'live';
  } else if (providerState === 'active' || operational === 'live') {
    headline = `Delivering on ${provider}`;
    tone = 'live';
  } else {
    headline = `Published to ${provider}`;
    tone = 'quiet';
  }

  const publishStage = sync.stages.find((stage) => stage.target === target && stage.stage_key === 'publish');
  const sinceIso = publishStage?.completed_at ?? block?.last_synced_at;
  const totalDays = Math.max(1, dayIndex(endDate, startDate) + 1);
  const dayOf = Math.min(totalDays, Math.max(0, dayIndex(todayStr, startDate) + 1));
  const sinceParts = (finished
    ? [`Ran ${fmtDayShort(startDate)} → ${fmtDayShort(lastDay)}`]
    : over
      ? []
      : [
          sinceIso ? `Since ${fmtDayShort(sinceIso)}` : undefined,
          dayOf > 0 ? `day ${dayOf} of ${totalDays}` : `starts ${fmtDayShort(startDate)}`,
        ]
  ).concat(deliveryCutoff ? `last report ${fmtDayShort(deliveryCutoff)}` : undefined)
    .filter((part): part is string => !!part);

  const groups = (sync.delivery_groups ?? []).filter((group) => group.target === target);
  const ads = (sync.ads ?? []).filter((ad) => ad.target === target);

  const rows: { label: string; value: string }[] = [];
  // A stopped campaign's ad sets and ads still read ACTIVE under its paused
  // parent; counting them "live" would say the opposite of the headline.
  const childrenTell = operational !== 'ended';
  if (childrenTell && sync.delivery_groups) {
    rows.push({
      label: target === 'meta' ? 'Ad sets' : 'Ad groups',
      value: bucketLine(groups.map((group) => providerStatusClass(group.provider_status, group.provider_delivery_group_id)), 'live'),
    });
  }
  if (childrenTell && target === 'meta' && sync.ads) {
    rows.push({
      label: 'Ads',
      value: bucketLine(ads.map((ad) => providerStatusClass(ad.provider_status, ad.provider_ad_id)), 'active'),
    });
  }
  const spend = asNumber(overview?.performance.gross_spend_cents);
  const impressions = asNumber(overview?.performance.impressions);
  const toDate = over ? '' : ' to date';
  if (spend !== null) rows.push({ label: `Gross spend${toDate}`, value: fmt.$full(spend / 100) });
  if (impressions !== null) rows.push({ label: `Impressions${toDate}`, value: Math.round(impressions).toLocaleString() });

  let pace: DeliverySummaryModel['pace'];
  const pacing = overview?.pacing;
  const actual = asNumber(pacing?.actual_gross_spend_cents);
  const planned = asNumber(pacing?.planned_spend_cents);
  if (finished && pacing && budget > 0) {
    // The final figure only once reporting covers the last flight day: the
    // server leaves the ratio null until then, and an unspent amount read off
    // a half-reported campaign would be a guess.
    // While pending, the pacing actual is null; the reported gross spend above
    // is what is known so far, never a 0% beside it (2026-10-19).
    const spentCents = actual ?? spend;
    const budgetCents = budget * 100;
    const settled = pacing.ratio !== null && pacing.ratio !== undefined && actual !== null;
    const unspent = budgetCents - (spentCents ?? 0);
    const sentence = settled && spentCents !== null
      ? `${fmt.$full(spentCents / 100)} of ${fmt.$full(budget)} spent${unspent > 0 ? ` · ${fmt.$full(unspent / 100)} unspent` : ''}`
      : spentCents !== null
        ? `${fmt.$full(spentCents / 100)} of ${fmt.$full(budget)} spent · final spend pending`
        : `${fmt.$full(budget)} budget · spend pending`;
    pace = {
      spentPct: spentCents === null ? null : Math.min(1, spentCents / budgetCents),
      sentence,
      tone: 'quiet',
    };
  } else if (pacing && actual !== null && budget > 0) {
    const spentPct = Math.min(1, actual / (budget * 100));
    const elapsedPct = Math.min(1, Math.max(0, dayOf / totalDays));
    const status = pacing.status;
    const plannedText = planned !== null ? ` of ${fmt.$full(planned / 100)} planned so far` : '';
    const sentence = status === 'behind'
      ? `Behind plan: ${fmt.$full(actual / 100)}${plannedText}.`
      : status === 'ahead'
        ? `Ahead of plan: ${fmt.$full(actual / 100)}${plannedText}.`
        : status === 'on_pace'
          ? `On plan: ${fmt.$full(actual / 100)}${plannedText}.`
          : `${fmt.$full(actual / 100)} of the ${fmt.$full(budget)} gross budget spent.`;
    pace = {
      spentPct,
      // An archived campaign stopped before its window ended; no "today" marker.
      elapsedPct: over ? undefined : elapsedPct,
      sentence,
      tone: status === 'behind' || status === 'ahead' ? 'warn' : status === 'on_pace' ? 'live' : 'quiet',
    };
  }

  return { headline, tone, since: sinceParts.join(' · '), rows, pace };
}

const TONE_COLOR: Record<DeliveryTone, string> = {
  live: 'var(--live)',
  quiet: 'var(--ink-3)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
};

export function CampaignDeliverySummary(props: CampaignDeliverySummaryProps) {
  const model = deliverySummaryModel(props);
  if (!model) return null;
  return (
    <div className="forecast delivery-summary" data-tone={model.tone}>
      <h4>
        <span className="delivery-dot" style={{ background: TONE_COLOR[model.tone] }} aria-hidden="true" />
        {model.headline}
      </h4>
      {model.since && <div className="muted delivery-since">{model.since}</div>}
      {model.rows.map((row) => (
        <div key={row.label} className="fc-row">
          <span>{row.label}</span>
          <b>{row.value}</b>
        </div>
      ))}
      {model.pace && (
        <div className="delivery-pace" role="group" aria-label="Budget pace">
          <div className="delivery-pace-head">
            <span className="fc-big-lbl" style={{ marginBottom: 0 }}>{model.pace.elapsedPct === undefined ? 'Budget' : 'Budget pace'}</span>
            <span className="mono tnum" style={{ fontSize: 10.5, color: TONE_COLOR[model.pace.tone], fontWeight: 600 }}>
              {model.pace.spentPct !== null && <>{Math.round(model.pace.spentPct * 100)}% spent</>}
              {model.pace.elapsedPct !== undefined && <> · day {Math.round(model.pace.elapsedPct * 100)}%</>}
            </span>
          </div>
          <div className="delivery-pace-bar" aria-hidden="true">
            <div className="delivery-pace-fill" style={{ width: `${Math.round((model.pace.spentPct ?? 0) * 100)}%` }} />
            {model.pace.elapsedPct !== undefined && (
              <div className="delivery-pace-today" style={{ left: `${Math.round(model.pace.elapsedPct * 100)}%` }} />
            )}
          </div>
          <div className="delivery-pace-sentence" style={{ color: TONE_COLOR[model.pace.tone] }}>{model.pace.sentence}</div>
        </div>
      )}
    </div>
  );
}
